import type { ComponentChildren } from "preact"
import { useEffect, useRef, useState } from "preact/hooks"
import { useMatchChannel } from "../hooks/useMatchChannel"
import { useRevealTimer } from "../hooks/useRevealTimer"
import type { MatchState, ResolvedQuestion, User } from "../types"
import { IconCheck, IconX } from "./Icons"
import {
	ApiError,
	acceptMatch,
	answerMatchQuestion,
	declineMatch,
} from "./lib/api"
import { QuestionBoard } from "./QuestionBoard"
import { QuestionPicture } from "./QuestionPicture"

interface MatchPlayProps {
	id: number
	me: User
}

// How long the just-resolved question stays on screen before the view moves
// on to the next question (or the final result, if that was the last one).
const REVEAL_MS = 2500

function otherOf(state: MatchState, meID: number): User {
	return state.challenger.id === meID ? state.opponent : state.challenger
}

function scoreOf(state: MatchState, userID: number): number {
	return state.scores[String(userID)] ?? 0
}

export function MatchPlay({ id, me }: MatchPlayProps) {
	const { state, error: channelError, reconnect } = useMatchChannel(id)
	const [actionError, setActionError] = useState("")
	const [responding, setResponding] = useState(false)
	const [answering, setAnswering] = useState(false)
	// A locally-submitted choice for the current question, shown immediately
	// while waiting for the SSE push that confirms it server-side.
	const [pendingChoice, setPendingChoice] = useState<{
		idx: number
		choice: number
	} | null>(null)
	const [reveal, setReveal] = useState<ResolvedQuestion | null>(null)
	const resolvedSeen = useRef(0)
	const initialized = useRef(false)
	const scheduleReveal = useRevealTimer(id)

	// Reset all per-match local state whenever `id` changes (the component is
	// also remounted via a `key` from App, but this keeps it correct even if
	// that ever changes).
	useEffect(() => {
		resolvedSeen.current = 0
		initialized.current = false
		setReveal(null)
		setPendingChoice(null)
	}, [id])

	// Detect a newly-resolved question (server moved current_question
	// forward because both players answered) and hold a brief reveal of it
	// before letting the live question underneath show through.
	useEffect(() => {
		if (!state) return
		const count = state.resolved.length
		if (!initialized.current) {
			initialized.current = true
			resolvedSeen.current = count
			return
		}
		if (count > resolvedSeen.current) {
			resolvedSeen.current = count
			setReveal(state.resolved[count - 1])
			scheduleReveal(REVEAL_MS, () => setReveal(null))
		}
	}, [state])

	// Clear the optimistic pending choice once the live question moves past
	// the one it was submitted for.
	useEffect(() => {
		if (!state?.question) {
			setPendingChoice(null)
			return
		}
		if (pendingChoice && pendingChoice.idx !== state.question.idx) {
			setPendingChoice(null)
		}
	}, [state, pendingChoice])

	async function handleAccept() {
		setActionError("")
		setResponding(true)
		try {
			await acceptMatch(id)
		} catch (e) {
			setActionError(
				e instanceof Error ? e.message : "Failed to accept challenge",
			)
		} finally {
			setResponding(false)
		}
	}

	async function handleDecline() {
		setActionError("")
		setResponding(true)
		try {
			await declineMatch(id)
		} catch (e) {
			setActionError(
				e instanceof Error ? e.message : "Failed to decline challenge",
			)
		} finally {
			setResponding(false)
		}
	}

	async function handleAnswer(choice: number) {
		if (
			!state?.question ||
			state.question.you_answered ||
			pendingChoice ||
			answering
		)
			return
		const idx = state.question.idx
		setAnswering(true)
		setActionError("")
		try {
			setPendingChoice({ idx, choice })
			await answerMatchQuestion(id, idx, choice)
		} catch (e) {
			setPendingChoice(null)
			if (e instanceof ApiError && e.status === 409) {
				// The SSE view has gone stale (the match advanced in another
				// tab, or the stream dropped and hasn't caught up). Reconnect to
				// get a fresh snapshot and snap the board onto the current
				// question rather than leaving it stuck on the old one.
				reconnect()
			} else {
				setActionError(
					e instanceof Error ? e.message : "Failed to submit answer",
				)
			}
		} finally {
			setAnswering(false)
		}
	}

	if (channelError && !state) {
		return (
			<div class="container page">
				<div class="alert warning">{channelError}</div>
			</div>
		)
	}

	if (!state) {
		return (
			<div class="flex h-100 center jc-center">
				<div class="spinner" role="status" />
			</div>
		)
	}

	const other = otherOf(state, me.id)

	// Header shared by the active and reveal views.
	const header = (
		<span class="flex gap-3 tnum small">
			<span class="truncate">
				<strong>{me.username}</strong> {scoreOf(state, me.id)} –{" "}
				{scoreOf(state, other.id)} <strong>{other.username}</strong>
			</span>
			<span class="muted nowrap">
				Question {(reveal ? reveal.idx : state.current_question) + 1} / 10
			</span>
		</span>
	)

	if (reveal) {
		const myAnswer = reveal.answers[String(me.id)]
		const otherAnswer = reveal.answers[String(other.id)]
		const winnerText =
			reveal.winner_id === null
				? "Nobody got it in time."
				: reveal.winner_id === me.id
					? "You won this one!"
					: `${other.username} won this one.`
		return (
			<QuestionBoard
				header={header}
				prompt={reveal.prompt}
				image={reveal.image}
				choices={reveal.choices}
				choiceClass={(i) => {
					if (i === reveal.correct_choice) return "success"
					if (i === myAnswer?.choice) return "danger"
					return "outline"
				}}
				disabled
				onAnswer={() => {}}
				footer={
					<div class="stack gap-1 mt-3">
						<p class="fw-semibold">{winnerText}</p>
						<p class="muted small">
							You:{" "}
							{myAnswer
								? `${myAnswer.is_correct ? "correct" : "wrong"}, ${myAnswer.ms}ms`
								: "no answer"}
							{" · "}
							{other.username}:{" "}
							{otherAnswer
								? `${otherAnswer.is_correct ? "correct" : "wrong"}, ${otherAnswer.ms}ms`
								: "no answer"}
						</p>
					</div>
				}
			/>
		)
	}

	if (state.status === "pending") {
		const iAmOpponent = state.opponent.id === me.id
		return (
			<div class="container page text-center">
				{/* .center so the buttons size to their labels instead of
				    stretching across the column. */}
				<div class="mx-auto stack center gap-3 pt-5" style="max-width: 28rem">
					{iAmOpponent ? (
						<>
							<h1>
								<strong>{state.challenger.username}</strong> challenged you to a{" "}
								{state.topic_name} match
							</h1>
							{actionError && <div class="alert danger">{actionError}</div>}
							<div class="flex gap-2 jc-center">
								<button
									type="button"
									class="success"
									disabled={responding}
									onClick={handleAccept}
								>
									Accept
								</button>
								<button
									type="button"
									disabled={responding}
									onClick={handleDecline}
								>
									Decline
								</button>
							</div>
						</>
					) : (
						<>
							<h1>
								Waiting for <strong>{state.opponent.username}</strong> to
								accept…
							</h1>
							{actionError && <div class="alert danger">{actionError}</div>}
							<button
								type="button"
								disabled={responding}
								onClick={handleDecline}
							>
								Cancel challenge
							</button>
						</>
					)}
					<a href="/">Back home</a>
				</div>
			</div>
		)
	}

	if (state.status === "declined") {
		return (
			<div class="container page text-center">
				<div class="mx-auto stack center gap-3 pt-5" style="max-width: 28rem">
					<h1>This challenge was declined.</h1>
					<a href="/">Back home</a>
				</div>
			</div>
		)
	}

	if (state.status === "finished") {
		const myScore = scoreOf(state, me.id)
		const otherScore = scoreOf(state, other.id)
		// A forfeit ends the match wherever it stood, so the score under the
		// headline can be 0-0 or even favour the loser. Say who walked away
		// instead of announcing a win the questions do not explain.
		const iForfeited = state.forfeited_by === me.id
		const forfeited = state.forfeited_by !== null
		const outcome = forfeited
			? iForfeited
				? `You forfeited. ${other.username} wins.`
				: `${other.username} forfeited. You win!`
			: state.winner_id === null
				? "Draw"
				: state.winner_id === me.id
					? "You won"
					: "You lost"
		// A forfeit reveals all ten questions, including the ones nobody ever
		// saw. Only list the ones that were actually played — an unanswered
		// question in the list would read as one both players lost.
		const played = forfeited
			? state.resolved.filter((q) => Object.keys(q.answers).length > 0)
			: state.resolved
		return (
			<div class="container page" style="max-width: 40rem">
				<div class="stack gap-1">
					<h1>{outcome}</h1>
					<p class="muted small">
						{state.topic_name}
						{forfeited &&
							` · ended at question ${state.current_question + 1} of 10`}
					</p>
					<p class="display sm tnum">
						{myScore} - {otherScore}
					</p>
				</div>
				<div class="flex wrap gap-2">
					<a href="/" class="btn">
						Home
					</a>
				</div>
				{played.map((q) => {
					const myAnswer = q.answers[String(me.id)]
					const otherAnswer = q.answers[String(other.id)]
					return (
						<div class="stack gap-1" key={q.idx}>
							<h3>
								{q.prompt}
								{q.image && (
									<QuestionPicture image={q.image} variant="inline" />
								)}
							</h3>
							{q.choices.map((c, i) => {
								const wrongPickers: string[] = []
								if (myAnswer?.choice === i && i !== q.correct_choice)
									wrongPickers.push("you")
								if (otherAnswer?.choice === i && i !== q.correct_choice)
									wrongPickers.push(other.username)
								let icon: ComponentChildren = null
								let tone = "muted"
								if (i === q.correct_choice) {
									icon = <IconCheck />
									tone = "text-success"
								} else if (wrongPickers.length > 0) {
									icon = <IconX />
									tone = "text-danger"
								}
								return (
									<div class={`flex center gap-2 small ${tone}`} key={i}>
										{icon}
										<span>{c}</span>
										{wrongPickers.length > 0 && (
											<span class="muted">{wrongPickers.join(", ")}</span>
										)}
									</div>
								)
							})}
						</div>
					)
				})}
			</div>
		)
	}

	// active
	const question = state.question
	if (!question) return null
	const youAnswered =
		question.you_answered || pendingChoice?.idx === question.idx
	const yourChoice = question.your_choice ?? pendingChoice?.choice ?? null

	return (
		<QuestionBoard
			header={header}
			prompt={question.prompt}
			image={question.image}
			choices={question.choices}
			// Your own pick stays filled in while the opponent is still answering.
			choiceClass={(i) => (i === yourChoice ? "primary" : "outline")}
			disabled={youAnswered}
			onAnswer={handleAnswer}
			footer={
				<div class="stack gap-2 mt-3">
					{youAnswered && (
						<p class="muted small">
							{question.opponent_answered
								? `Waiting for the result…`
								: `Waiting for ${other.username}…`}
						</p>
					)}
					{actionError && <div class="alert danger small">{actionError}</div>}
				</div>
			}
		/>
	)
}
